import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from '../snackbar/SnackbarContext';

import { useConfig } from '../ConfigContext';
import { del, get, post, setToken, request } from '../utils/Request';
import { Availability,
  BhvrUser,
  CodePayload,
  SigninPayload,
  SigninWithProviderPayload,
  SignupPayload,
  UserContext,
  TokenResponse,
  PlayerRegistrationResponse } from './Authentication.model';
import { RequestError } from '../Error.model';
import { useRedirectUrl } from '../redirect-url/ProviderRedirectUrlContext';
import { useGameTheme } from '../GameThemeContext';

const AuthenticationContext = createContext<UserContext>({
  user: null,
  isUserLoad: false,
  signin: null,
  signinWithProvider: null,
  getPlayer: null,
  checkPlayerAvailability: null,
  logout: null,
  updateUser: null,
  register: null,
});

export const useAuth = (): UserContext => useContext(AuthenticationContext);

const useProvideAuth = (): UserContext => {
  const history = useHistory();
  const snackbar = useSnackbar();
  const [user, setUser] = useState<BhvrUser>(null);
  const [isUserLoad, setIsUserLoad] = useState(false);
  const redirect = useRedirectUrl();
  const theme = useGameTheme();

  const config = useConfig();

  const displayError = (err: RequestError): void => {
    snackbar.addErrorMessage(err.code);
  };

  const getPlayer = async (): Promise<void> => {
    try {
      const response = await request<BhvrUser>(
        'GET',
        `${config.clientConfig.host}/players/me`,
        { disableRedirect: true },
      );
      setUser(response);
    } catch (e) {
      const err = e as RequestError;
      if (err.message !== 'Authentication required') {
        displayError(err);
      }
    }

    setIsUserLoad(true);
    if (redirect.path) {
      history.replace(redirect.path);
      redirect.setRedirect('');
    }
  };

  const checkPlayerAvailability = async (email: string): Promise<Availability> => {
    const available = await get<Availability>(`${config.clientConfig.host}/players/check-email/${email}`);
    return available;
  };

  const signinWithProvider = async (
    payload: SigninWithProviderPayload,
    type: string,
    email: string,
    code?: CodePayload,
  ): Promise<void> => {
    let body = {
      idToken: payload.idToken,
    };

    if (code) {
      body = {
        ...body,
        ...code,
      };
    }

    try {
      const resp: TokenResponse = await post(`${config.clientConfig.host}/auth/${type}`, { data: body });
      setToken(resp.token, resp.expiresIn);
      getPlayer();
    } catch (e) {
      const err = e as RequestError;
      if (err.message === 'Player not found') {
        history.push(theme?.gameId ? `/register/${theme?.gameId}` : '/register', {
          email,
          idToken: payload.idToken,
          type,
        });
      } else if (err.message === 'Code is required') {
        history.push(theme?.gameId ? `/auth/otp/${theme?.gameId}` : '/auth/otp', {
          provider: type,
          idToken: payload.idToken,
          isEmailVerified: err.data.isEmailVerified,
          expiryDate: err.data.expiryDate,
          hash: err.data.hash,
        });
      } else {
        displayError(err);
      }
    }
  };

  const signin = async (
    payload: SigninPayload,
    reCaptchaToken: string,
    code?: CodePayload,
  ): Promise<void> => {
    let headers = {};
    if (reCaptchaToken) {
      headers = {
        'g-recaptcha-response': reCaptchaToken,
      };
    }

    let body = {
      ...payload,
    };

    if (code) {
      body = {
        ...body,
        ...code,
      };
    }

    try {
      const resp: TokenResponse = await post(`${config.clientConfig.host}/auth/password`, { data: body, headers });

      setToken(resp.token, resp.expiresIn);
      getPlayer();
    } catch (e) {
      const err = e as RequestError;
      if (err.message === 'Code is required') {
        history.push(theme?.gameId ? `/auth/otp/${theme?.gameId}` : '/auth/otp', {
          email: payload.email,
          password: payload.password,
          isEmailVerified: err.data.isEmailVerified,
          expiryDate: err.data.expiryDate,
          hash: err.data.hash,
        });
      } else {
        displayError(err);
      }
    }
  };

  const logout = async (): Promise<void> => {
    try {
      theme.setGameId('');
      await del(`${config.clientConfig.host}/auth`);
      setUser(null);
      redirect.setRedirect('');
    } catch (e) {
      const err = e as RequestError;
      snackbar.addErrorMessage(err.code);
    }
  };

  const updateUser = (newUser: BhvrUser): void => {
    setUser(newUser);
  };

  const register = async (
    payload: SignupPayload,
    email: string,
    password: string,
    reCaptchaToken?: string,
  ): Promise<void> => {
    let headers = {};

    if (reCaptchaToken) {
      headers = {
        'g-recaptcha-response': reCaptchaToken,
      };
    }

    try {
      const response = await post<PlayerRegistrationResponse>(
        `${config.clientConfig.host}/players`,
        { data: payload, headers },
      );
      setToken(response.token?.token, response.token.expiresIn);
      setUser(response.player);

      if (redirect.path) {
        history.replace(redirect.path);
        redirect.setRedirect('');
      }
    } catch (e) {
      const err = e as RequestError;
      if (err.message === 'Code is required') {
        history.push(theme?.gameId ? `/auth/otp/${theme?.gameId}` : '/auth/otp', {
          email,
          password,
          isEmailVerified: err.data.isEmailVerified,
          expiryDate: err.data.expiryDate,
          hash: err.data.hash,
        });
      } else {
        snackbar.addErrorMessage(err.code);
      }
    }
  };

  useEffect(() => {
    getPlayer();
  }, []);

  return {
    user,
    isUserLoad,
    signin,
    signinWithProvider,
    getPlayer,
    checkPlayerAvailability,
    logout,
    updateUser,
    register,
  };
};

interface Props {
  children?: JSX.Element | JSX.Element[];
}

export function ProvideAuthentication(props: Props): JSX.Element {
  const auth = useProvideAuth();

  return (
    <AuthenticationContext.Provider value={auth}>
      {props.children}
    </AuthenticationContext.Provider>
  );
}
